import React, { useEffect, useState } from "react";
import CustomBreadCrumb from "components/CustomBreadCrumb";
import { Link, useParams } from "react-router-dom";
import { axiosInstance } from "config/axiosConfig";
import { API_ENDPOINTS, FILE_BASE_URL } from "config/apiConfig";
import {
  formatProductQuantity,
  getLabelFromValue,
  productPrice,
} from "common/utils";
import {
  DISPOSAL_TYPE,
  PRODUCT_CATEGORY,
  PRODUCT_STATE,
  SHIP_CHARGE_TYPE,
  SHIP_DAY,
} from "common/constants";
import { IconDirectorySyncBlue, IconKeyboardArrowRightBlue } from "assets/svg";
import { Button, Input, message, Modal, Select } from "antd";
import { UTCToJapanTime } from "common/helper/time.helper";
import { NoteTypeEnum } from "common/enums/NoteType.enum";
import { useCommonContext } from "contexts/CommonContext";
import {
  getStatusOptions,
  ProductStatusEnum,
  ProductStatusMap,
} from "common/enums/Product.enum";

type AdminNote = {
  id: string | number;
  created_at: string;
  content: string;
};

const CompanyManagementProductViewPage = () => {
  const { setHeaderTitle } = useCommonContext();
  const { companyId, productCode } = useParams();
  const [productDetail, setProductDetail] = useState<any>({});
  const [companyDetail, setCompanyDetail] = useState<any>({});
  const [noteList, setNoteList] = useState<AdminNote[]>([]);
  const [adminNote, setAdminNote] = useState<string>("");
  const [isStatusModalVisible, setIsStatusModalVisible] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<number | undefined>(
    undefined,
  );

  const fetchProductDetail = () => {
    axiosInstance
      .get(API_ENDPOINTS.PRODUCT_DETAIL + `/${productCode}`)
      .then((res) => {
        setProductDetail(res.data.data);
        setCompanyDetail(res.data.data.company);
      });
  };

  useEffect(() => {
    fetchProductDetail();
    setHeaderTitle(
      `出品詳細（出品ID：#${productDetail?.code || productCode}）`,
    );
  }, []);

  const fetchNoteList = () => {
    if (!productDetail.id) return;
    axiosInstance
      .get(API_ENDPOINTS.NOTE_LIST, {
        params: {
          sort: "id:asc",
          object_id: productDetail.id,
          object_type: NoteTypeEnum.PRODUCT,
        },
      })
      .then((res) => {
        setNoteList(res.data.data || []);
      });
  };

  const handleSaveNote = () => {
    if (!adminNote.trim()) return;

    axiosInstance
      .post(API_ENDPOINTS.NOTE_LIST, {
        object_id: productDetail.id,
        object_type: NoteTypeEnum.PRODUCT,
        content: adminNote,
      })
      .then((res) => {
        if (res.status === 201) {
          setAdminNote("");
          fetchNoteList();
          message.success("保存に成功しました");
        }
      })
      .catch(() => {
        message.error("保存に失敗しました");
      });
  };

  const handlePrivateProduct = () => {
    fetchPrivateProduct();
  };

  const fetchPrivateProduct = () => {
    axiosInstance
      .put(API_ENDPOINTS.EDIT_PRODUCT + `/${productCode}`, {
        status: ProductStatusEnum.PRIVATE,
      })
      .then((res) => {
        if (res.status === 200) {
          message.success("商品の状態が変更されました");
          setProductDetail(res.data.data);
        }
      });
  };

  const handleStatusChange = () => {
    setSelectedStatus(productDetail?.status);
    setIsStatusModalVisible(true);
  };

  const handleStatusUpdate = async () => {
    if (
      selectedStatus === undefined ||
      selectedStatus === productDetail?.status
    ) {
      message.warning("新しいステータスを選択してください");
      return;
    }

    try {
      const response = await axiosInstance.put(
        API_ENDPOINTS.EDIT_PRODUCT + `/${productCode}`,
        {
          status: selectedStatus,
        },
      );

      if (response.status === 200) {
        message.success("商品のステータスが変更されました");
        setProductDetail(response.data.data);
        setIsStatusModalVisible(false);
        setSelectedStatus(undefined);
      }
    } catch (error) {
      message.error("ステータスの更新に失敗しました");
    }
  };

  useEffect(() => {
    fetchNoteList();
  }, [productDetail.id]);

  return (
    <div className="self-stretch min-h-[924px] p-[20px] pb-[100px] md:p-10 bg-white inline-flex flex-col justify-start items-start gap-[30px] md:gap-12 overflow-hidden">
      <CustomBreadCrumb
        items={[
          {
            label: "ユーザー管理",
            link: "/admin/company-management",
          },
          {
            label: `出品一覧`,
            link: `/admin/company-management/${companyId}/products`,
          },
          {
            label: `出品詳細（出品ID: #${productDetail?.code || productCode}）`,
            isCurrent: true,
          },
        ]}
      />

      <div className="flex flex-col items-start gap-5 self-stretch">
        <div
          data-プロパティ1="base"
          className="self-stretch py-2.5 border-b border-neutral-300 inline-flex justify-start items-center gap-3.5"
        >
          <div className="w-1 self-stretch bg-blue-900 rounded-sm" />
          <div className="flex-1 flex justify-start items-center gap-2.5">
            <div className="flex justify-start items-center gap-[5px]">
              <div className="justify-start text-blue-900 text-[18px] md:text-[22px] font-bold font-['Noto_Sans_JP'] leading-9">
                出品画像
                <span className="text-[12px] font-medium">(最大5枚)</span>
              </div>
            </div>
          </div>
        </div>
        <div className="flex max-w-[100%] gap-[10px] w-full">
          {productDetail?.images && productDetail.images.length > 0 ? (
            productDetail.images.map((image: any, index: number) => (
              <img
                key={`${image.id}-${index}`}
                className="max-w-[20%] aspect-square object-cover rounded"
                src={`${FILE_BASE_URL}/${image.image_url}`}
                alt={`Product image ${index + 1}`}
              />
            ))
          ) : (
            <div className="max-w-[20%] aspect-square bg-gray-200 flex items-center justify-center rounded w-full">
              <span className="text-gray-500 text-sm">画像なし</span>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col items-start gap-5 self-stretch">
        <div
          data-プロパティ1="base"
          className="self-stretch py-2.5 border-b border-neutral-300 inline-flex justify-start items-center gap-3.5"
        >
          <div className="w-1 self-stretch bg-blue-900 rounded-sm" />
          <div className="flex-1 flex justify-start items-center gap-2.5">
            <div className="flex justify-start items-center gap-[5px]">
              <div className="justify-start text-blue-900 text-[18px] md:text-[22px] font-bold font-['Noto_Sans_JP'] leading-9">
                商品の基本情報
              </div>
            </div>
          </div>
        </div>
        <div className="border border-neutral-border rounded-[6px] overflow-hidden w-full">
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              出品ID
            </div>
            <div className="flex-1 p-4">{productDetail?.code}</div>
          </div>
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              商品名
            </div>
            <div className="flex-1 p-4">{productDetail?.product_name}</div>
          </div>
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              カテゴリー
            </div>
            <div className="flex-1 p-4">
              {getLabelFromValue(PRODUCT_CATEGORY, productDetail?.category_id)}
            </div>
          </div>
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              数量
            </div>
            <div className="flex-1 p-4">
              {formatProductQuantity(productDetail)}
            </div>
          </div>
          <div className="flex">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              ステータス
            </div>
            <div className="flex-1 p-4 justify-between flex">
              <span>{ProductStatusMap[productDetail?.status] || ""}</span>
              <span
                className="text-primary cursor-pointer"
                onClick={handleStatusChange}
              >
                {" "}
                <IconDirectorySyncBlue className="inline-block" />{" "}
                ステータスを変更する
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col items-start gap-5 self-stretch">
        <div
          data-プロパティ1="base"
          className="self-stretch py-2.5 border-b border-neutral-300 inline-flex justify-start items-center gap-3.5"
        >
          <div className="w-1 self-stretch bg-blue-900 rounded-sm" />
          <div className="flex-1 flex justify-start items-center gap-2.5">
            <div className="flex justify-start items-center gap-[5px]">
              <div className="justify-start text-blue-900 text-[18px] md:text-[22px] font-bold font-['Noto_Sans_JP'] leading-9">
                出品詳細
              </div>
            </div>
          </div>
        </div>
        <div className="border border-neutral-border rounded-[6px] overflow-hidden w-full">
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              処理区分
            </div>
            <div className="flex-1 p-4">
              {getLabelFromValue(DISPOSAL_TYPE, productDetail?.disposal_type)}
            </div>
          </div>
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              商品の状態
            </div>
            <div className="flex-1 p-4">
              {getLabelFromValue(PRODUCT_STATE, productDetail?.state)}
            </div>
          </div>
          <div className="flex">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              商品の説明
            </div>
            <div className="flex-1 p-4">{productDetail?.description}</div>
          </div>
        </div>
      </div>
      <div className="flex flex-col items-start gap-5 self-stretch">
        <div
          data-プロパティ1="base"
          className="self-stretch py-2.5 border-b border-neutral-300 inline-flex justify-start items-center gap-3.5"
        >
          <div className="w-1 self-stretch bg-blue-900 rounded-sm" />
          <div className="flex-1 flex justify-start items-center gap-2.5">
            <div className="flex justify-start items-center gap-[5px]">
              <div className="justify-start text-blue-900 text-[18px] md:text-[22px] font-bold font-['Noto_Sans_JP'] leading-9">
                配送について
              </div>
            </div>
          </div>
        </div>
        <div className="border border-neutral-border rounded-[6px] overflow-hidden w-full">
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              配送料の負担
            </div>
            <div className="flex-1 p-4">
              {getLabelFromValue(
                SHIP_CHARGE_TYPE,
                productDetail?.shipping_charge_type,
              )}
            </div>
          </div>
          <div className="flex">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              発送までの日数
            </div>
            <div className="flex-1 p-4">
              {" "}
              {getLabelFromValue(SHIP_DAY, productDetail?.shipping_days)}
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col items-start gap-5 self-stretch">
        <div
          data-プロパティ1="base"
          className="self-stretch py-2.5 border-b border-neutral-300 inline-flex justify-start items-center gap-3.5"
        >
          <div className="w-1 self-stretch bg-blue-900 rounded-sm" />
          <div className="flex-1 flex justify-start items-center gap-2.5">
            <div className="flex justify-start items-center gap-[5px]">
              <div className="justify-start text-blue-900 text-[18px] md:text-[22px] font-bold font-['Noto_Sans_JP'] leading-9">
                販売価格
              </div>
            </div>
          </div>
        </div>
        <div className="border border-neutral-border rounded-[6px] overflow-hidden w-full">
          <div className="flex">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              価格
            </div>
            <div className="flex-1 p-4">{productPrice(productDetail)}</div>
          </div>
        </div>
      </div>
      <div className="flex flex-col items-start gap-5 self-stretch">
        <div
          data-プロパティ1="base"
          className="self-stretch py-2.5 border-b border-neutral-300 inline-flex justify-start items-center gap-3.5"
        >
          <div className="w-1 self-stretch bg-blue-900 rounded-sm" />
          <div className="flex-1 flex justify-start items-center gap-2.5">
            <div className="flex justify-start items-center gap-[5px]">
              <div className="justify-start text-blue-900 text-[18px] md:text-[22px] font-bold font-['Noto_Sans_JP'] leading-9">
                出品ユーザー情報
              </div>
            </div>
          </div>
        </div>
        <div className="border border-neutral-border rounded-[6px] overflow-hidden w-full">
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              ユーザーID
            </div>
            <div className="flex-1 p-4">{companyDetail?.code}</div>
          </div>
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              法人名
            </div>
            <div className="flex-1 p-4">{companyDetail?.company_name}</div>
          </div>
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              担当者名
            </div>
            <div className="flex-1 p-4">
              {companyDetail?.contact_first_name +
                " " +
                companyDetail?.contact_last_name}
            </div>
          </div>
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              メールアドレス
            </div>
            <div className="flex-1 p-4">{companyDetail?.email}</div>
          </div>
          <div className="flex border-b border-b-neutral-border">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              電話番号
            </div>
            <div className="flex-1 p-4">{companyDetail?.phone_number}</div>
          </div>
          <div className="flex">
            <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
              ステータス
            </div>
            <div className="flex-1 p-4">
              {ProductStatusMap[productDetail?.status] || ""}
            </div>
          </div>
        </div>
        <Link
          to={`/admin/company-management/${companyDetail?.code}/view`}
          className="text-[13px] text-primary font-bold"
        >
          このユーザーの詳細を見る
          <IconKeyboardArrowRightBlue className="inline" />
        </Link>
      </div>
      <div className="border border-neutral-border rounded-[6px] overflow-hidden w-full">
        {noteList.length === 0 ? (
          <div className="flex justify-center items-center p-8 text-neutral-500">
            操作ログがありません
          </div>
        ) : (
          noteList.map((note: AdminNote, idx: number) => {
            const { year, month, day, hour, minute } = UTCToJapanTime(
              note.created_at,
            );
            return (
              <div
                className={`flex ${idx !== noteList.length - 1 ? "border-b border-b-neutral-border" : ""}`}
                key={note.id}
              >
                <div className="w-[130px] md:w-[240px] p-4 font-bold border-r border-r-neutral-border">
                  {`${year}/${month}/${day} ${hour}:${minute}`}
                </div>
                <div className="flex-1 p-4">{note.content}</div>
              </div>
            );
          })
        )}
      </div>
      <div className="w-[100%]">
        <div className="text-[18px] md:text-[20px] font-bold leading-9 mb-[15px]">
          管理者メモを追加
        </div>
        <Input.TextArea
          placeholder="例：メール返信が3日間ないため注意フラグ／画像に他社ロゴが写っていたため非公開対応 etc."
          rows={9}
          className="text-base mb-3.5"
          value={adminNote}
          onChange={(e) => setAdminNote(e.target.value)}
        />

        <div className="flex justify-end gap-2.5 mb-4">
          <Button
            className="min-h-[50px] w-[340px] bg-primary text-white text-[16px] font-bold hover:bg-[#103E75]"
            onClick={handleSaveNote}
            disabled={!adminNote.trim()}
          >
            保存する
          </Button>
        </div>

        <div className="flex gap-2.5">
          <Link
            to={`/admin/company-management/${companyId}/products`}
            className="flex justify-center items-center rounded-[6px] min-h-[50px] w-[340px] bg-primary-light text-primary text-[16px] font-bold border-none"
          >
            一覧へ戻る
          </Link>
          {productDetail.status !== ProductStatusEnum.PRIVATE ? (
            <Button
              onClick={handlePrivateProduct}
              className="cursor-pointer flex items-center justify-center w-[340px] rounded-[6px] min-h-14 bg-white outline outline-2 outline-offset-[-2px] outline-red-600 text-red-600 text-lg font-bold hover:bg-red-600 hover:text-white"
            >
              非公開にする
            </Button>
          ) : (
            ``
          )}
        </div>
      </div>

      {/* Status Change Modal */}
      <Modal
        open={isStatusModalVisible}
        onCancel={() => {
          setIsStatusModalVisible(false);
          setSelectedStatus(undefined);
        }}
        footer={null}
        closable={false}
        centered
        width={600}
        className="!p-0 !rounded-[30px] !shadow-[0px_0px_15px_0px_rgba(0,0,0,0.05)] !outline !outline-8 !outline-offset-[-8px] !outline-white"
      >
        <div className="md:px-10 md:py-16 flex flex-col justify-center items-center gap-10 overflow-hidden">
          <div className="w-full flex flex-col justify-start items-center gap-7">
            <div className="w-full flex flex-col justify-start items-start gap-5">
              <div className="w-full whitespace-pre-line text-center text-primary text-[22px] md:text-3xl font-bold font-['Noto_Sans_JP'] leading-10">
                商品ステータスを変更
              </div>

              <div className="w-full flex flex-col gap-4">
                <div className="w-full text-center text-black text-[15px] md:text-base font-normal font-['Noto_Sans_JP'] leading-relaxed">
                  現在のステータス:{" "}
                  <strong className="text-primary">
                    {ProductStatusMap[productDetail?.status] || ""}
                  </strong>
                </div>

                <div className="w-full text-center text-black text-[15px] md:text-base font-normal font-['Noto_Sans_JP'] leading-relaxed">
                  新しいステータスを選択してください:
                </div>

                <div className="w-full px-4">
                  <Select
                    value={selectedStatus}
                    onChange={setSelectedStatus}
                    placeholder="ステータスを選択"
                    className="w-full [&_.ant-select-selector]:!h-14 [&_.ant-select-selector]:!border-2 [&_.ant-select-selector]:!border-primary [&_.ant-select-selector]:!rounded-md"
                    size="large"
                    options={getStatusOptions()}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="w-full flex flex-col justify-start items-center gap-3.5">
            <button
              onClick={handleStatusUpdate}
              disabled={
                selectedStatus === undefined ||
                selectedStatus === productDetail?.status
              }
              className={`w-80 min-h-14 px-5 py-2.5 rounded-md flex justify-center items-center text-white text-lg font-bold font-['Noto_Sans_JP'] leading-7 transition-all duration-200 ${
                selectedStatus === undefined ||
                selectedStatus === productDetail?.status
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-primary hover:bg-primary/90"
              }`}
            >
              変更する
            </button>
            <button
              onClick={() => {
                setIsStatusModalVisible(false);
                setSelectedStatus(undefined);
              }}
              className="text-primary text-sm font-medium font-['Noto_Sans_JP'] underline leading-snug hover:opacity-80 transition-opacity"
            >
              キャンセル
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CompanyManagementProductViewPage;
